import { BlockDef } from "./block.interfaces";
import { useI18n } from "vue-i18n";
import { useDefaultColors } from "./useDefaultColors";
import {
    arrayField,
    createBlock,
    descriptiveImageField,
    headingField,
    htmlField,
    textField,
} from "./blockBuilder";
import { FeaturesBaseProps } from "@/views/blocks/features/features.interfaces";
import { Component, computed } from "vue";
import { WithProductScreenShot } from "@/views/blocks/features/features";
import screenshotImage from "@/views/blocks/features/shared/images/page-left.png";
import featureIcon1 from "@/views/blocks/features/shared/images/feature1.svg";
import featureIcon2 from "@/views/blocks/features/shared/images/feature2.svg";
import featureIcon3 from "@/views/blocks/features/shared/images/feature3.svg";

export function useFeatures() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();

    function createBaseFeaturesBlock({
        component,
        typeId,
        name,
        extendBlock,
    }: {
        component: Component;
        typeId: string;
        name: string;
        extendBlock?: (block: ReturnType<typeof createBlock>) => ReturnType<typeof createBlock>;
    }): BlockDef {
        let block = createBlock<FeaturesBaseProps>({
            typeId,
            type: "features",
            name: t(name),
            component,
        })
            .withField("tag", textField("Tag", "Innovative Solutions", "Text"))
            .withField("title", headingField("Title", "Transform Your Business", "h2"))
            .withField(
                "description",
                htmlField(
                    "Description",
                    "Our cutting-edge platform empowers businesses to streamline operations, enhance productivity, and achieve sustainable growth in today's competitive market."
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    "Features",
                    "features",
                    {
                        iconSrc: {
                            label: "Icon",
                            type: "image",
                        },
                        title: {
                            label: "Title",
                            type: "text",
                        },
                        description: {
                            label: "Description",
                            type: "text",
                        },
                    },
                    [
                        {
                            iconSrc: featureIcon1,
                            title: "Seamless Integration",
                            description:
                                "Connect all your existing tools and systems effortlessly with our platform, eliminating data silos and creating a unified workflow experience.",
                        },
                        {
                            iconSrc: featureIcon2,
                            title: "Advanced Security",
                            description:
                                "Protect your valuable business data with enterprise-grade security features including end-to-end encryption and multi-factor authentication.",
                        },
                        {
                            iconSrc: featureIcon3,
                            title: "Automated Reporting",
                            description:
                                "Generate comprehensive insights and analytics automatically, helping you make data-driven decisions to optimize your business performance.",
                        },
                    ]
                )
            )
            .withField(
                "image",
                descriptiveImageField("Image", { src: screenshotImage, alt: "Feature image" })
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ]);

        if (extendBlock) {
            block = extendBlock(block);
        }

        return block.build();
    }

    const withProductScreenShotBlock = computed<BlockDef>(() =>
        createBaseFeaturesBlock({
            typeId: "WithProductScreenShot",
            name: "With product screenshot",
            component: WithProductScreenShot,
        })
    );

    return {
        blocks: computed(() => [withProductScreenShotBlock.value]),
    };
}
